import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import PropTypes from "prop-types";
import LoadingSpinner from "./LoadingSpinner";
import { ROUTES } from "components/App/AppConfig";

/**
 * ProtectedRoute component - Renders a route that requires authentication and permission checking
 *
 * This component handles:
 * - Authentication verification (redirects to login if not signed in)
 * - Async permission checking (e.g., admin or publisher status)
 * - Loading states during permission verification
 * - Error handling with fallback redirect
 *
 * @param {boolean} isSignedIn - Whether the user is authenticated
 * @param {Function} checkPermission - Async function that returns true/false for permission
 * @param {React.Component} component - The component to render if permission is granted
 * @param {string} redirectPath - Path to redirect to if permission is denied (default: '/')
 * @param {string} loadingMessage - Message to display during permission check
 * @returns {JSX.Element} The protected component or redirect
 *
 * @example
 * <ProtectedRoute
 *   isSignedIn={isSignedIn}
 *   checkPermission={getAdminStatus}
 *   component={AdminPage}
 *   loadingMessage="Verifying admin access..."
 * />
 */
function ProtectedRoute({
  isSignedIn,
  checkPermission,
  component: Component,
  redirectPath = ROUTES.ROOT,
  loadingMessage = "Verifying access...",
}) {
  const [hasPermission, setHasPermission] = useState(null); // null = loading state
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchPermission = async () => {
      try {
        const status = await checkPermission();
        setHasPermission(status);
      } catch (err) {
        console.error("Error checking permission:", err);
        setError(true);
      }
    };

    if (isSignedIn) {
      fetchPermission();
    }
  }, [isSignedIn, checkPermission]);

  // Not authenticated - redirect to login
  if (!isSignedIn) {
    return <Redirect to={ROUTES.LOGIN} />;
  }

  // Still checking permissions - show loading
  if (hasPermission === null) {
    return <LoadingSpinner message={loadingMessage} />;
  }

  // Permission denied or error - redirect to specified path
  if (error || !hasPermission) {
    return <Redirect to={redirectPath} />;
  }

  // Permission granted - render the component
  return <Component />;
}

ProtectedRoute.propTypes = {
  isSignedIn: PropTypes.bool.isRequired,
  checkPermission: PropTypes.func.isRequired,
  component: PropTypes.elementType.isRequired,
  redirectPath: PropTypes.string,
  loadingMessage: PropTypes.string,
};

export default ProtectedRoute;
